import { Checklist, checklistProgress } from "@rosc/ui/v32/checklist";
import type { JSX } from "react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { BetaFeedbackButton, BetaMark } from "@/components/ui/beta/Beta";
import { CHROME_WEB_STORE_URL } from "@/lib/chrome-extension";
import { useFeature, useIsBeta } from "@/lib/early-access";
import {
  CHECKLIST_ITEMS,
  type ChecklistItemId,
  isChecklistOpen,
  OPTIONAL_ITEMS,
} from "@/lib/guided/checklist";
import { trackOnboarding } from "@/lib/guided/events";
import { useAuthStore } from "@/store/auth.store";
import { useGuidedChecklistStore } from "@/store/guidedChecklist.store";

// "First steps" on Visits (guided first visit): above the list on phones and
// at the top of the Visits home on desktop, until every step but the
// optional extension is done, or it is closed.

export const GuidedChecklist = ({
  onReopenWelcome,
  onStartPractice,
  practiceVisitId,
  onOpenVisit,
  className,
}: {
  onReopenWelcome: () => void;
  onStartPractice: () => void;
  /** The practice consulta, once it is in the list. */
  practiceVisitId: string | null;
  onOpenVisit: (visitPublicId: string) => void;
  className?: string;
}): JSX.Element | null => {
  const { t } = useTranslation();
  const account = useAuthStore((state) => state.userData?.public_id ?? null);
  const state = useGuidedChecklistStore((store) => store.state);
  const loadedFor = useGuidedChecklistStore((store) => store.account);
  const guided = useFeature("guided-first-visit");
  const beta = useIsBeta("guided-first-visit");
  useEffect(() => {
    useGuidedChecklistStore.getState().load(account);
  }, [account]);
  if (!guided || loadedFor !== account) return null;
  if (!isChecklistOpen(state)) return null;

  const openPractice = (): void => {
    if (practiceVisitId) onOpenVisit(practiceVisitId);
    else onStartPractice();
  };
  const actions: Record<ChecklistItemId, (() => void) | undefined> = {
    setup: onReopenWelcome,
    practice: openPractice,
    review: openPractice,
    copy: openPractice,
    extension: () => {
      trackOnboarding("extension_install_clicked", { from: "checklist" });
      window.open(CHROME_WEB_STORE_URL, "_blank", "noopener");
      useGuidedChecklistStore.getState().complete("extension");
    },
  };
  const items = CHECKLIST_ITEMS.map((id) => ({
    id,
    label:
      id === "practice" && practiceVisitId && !state.done.includes(id)
        ? t("guided.checklist.practiceOpen")
        : t(`guided.checklist.${id}`),
    description:
      id === "practice" && !practiceVisitId
        ? t("guided.checklist.practiceHint")
        : undefined,
    done: state.done.includes(id),
    optional: OPTIONAL_ITEMS.has(id),
    onSelect: actions[id],
  }));
  const { done, total } = checklistProgress(items);
  return (
    <Checklist
      className={className}
      dismissLabel={t("guided.checklist.dismiss")}
      items={items}
      meterLabel={t("guided.checklist.title")}
      optionalLabel={t("guided.checklist.optional")}
      progressLabel={t("guided.checklist.progress", { done, total })}
      footer={
        beta ? (
          <BetaFeedbackButton
            className="w-full justify-start"
            feature="guided-first-visit"
          />
        ) : undefined
      }
      testId="guided-checklist"
      title={
        beta ? (
          <span className="inline-flex items-center gap-2">
            {t("guided.checklist.title")}
            <BetaMark feature="guided-first-visit" />
          </span>
        ) : (
          t("guided.checklist.title")
        )
      }
      onDismiss={() => useGuidedChecklistStore.getState().dismiss()}
    />
  );
};
